import React, { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { Search, ArrowLeft } from 'lucide-react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import BlogPost from '../components/BlogPost'
import { Post, Category } from '../types'
import { api } from '../services/api'

const SearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '')
  const [searchResults, setSearchResults] = useState<Post[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  useEffect(() => {
    fetchCategories()
    const query = searchParams.get('q')
    if (query) {
      setSearchQuery(query)
      performSearch(query)
    }
  }, [])

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories')
      setCategories(Array.isArray(response.data) ? response.data : [])
    } catch (error) {
      console.error('Error fetching categories:', error)
      setCategories([])
    }
  }

  const performSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([])
      setHasSearched(false)
      return
    }

    try {
      setLoading(true)
      const response = await api.get(`/search?q=${encodeURIComponent(query)}`)
      setSearchResults(Array.isArray(response.data) ? response.data : [])
      setHasSearched(true)
    } catch (error) {
      console.error('Error searching posts:', error)
      setSearchResults([])
      setHasSearched(true)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      setSearchParams({ q: searchQuery.trim() })
      performSearch(searchQuery.trim())
    }
  }

  const getCategoryForPost = (categoryId: string) => {
    return categories.find(cat => cat.id === categoryId)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center mb-6">
            <Link to="/">
              <Button variant="ghost" className="mr-4">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
          
          <div className="max-w-2xl">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Search Excel Content
            </h1>
            
            {/* Search Form */}
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search Excel tips, tutorials, formulas..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12 text-lg"
                />
              </div>
              <Button type="submit" variant="excel" size="lg" disabled={loading}>
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="container mx-auto px-4 py-12">
        {hasSearched && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Search Results
            </h2>
            <p className="text-gray-600">
              {loading 
                ? 'Searching...' 
                : `Found ${searchResults.length} result${searchResults.length !== 1 ? 's' : ''} for "${searchParams.get('q')}"`
              }
            </p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-excel-green mx-auto"></div>
              <p className="mt-4 text-gray-600">Searching...</p>
            </div>
          </div>
        ) : hasSearched ? (
          searchResults.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {searchResults.map((post) => {
                const category = getCategoryForPost(post.category_id)
                return (
                  <BlogPost 
                    key={post.id} 
                    post={post} 
                    category={category}
                  />
                )
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-xl font-bold text-gray-900 mb-4">No Results Found</h3>
              <p className="text-gray-600 mb-6">
                We couldn't find any posts matching "{searchParams.get('q')}". Try different keywords or browse our categories.
              </p>
              <Link to="/blog">
                <Button variant="excel">View All Posts</Button>
              </Link>
            </div>
          )
        ) : (
          <div className="text-center py-12">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Start Your Search</h3>
            <p className="text-gray-600 mb-6">
              Enter keywords to find Excel tutorials, tips, and insights.
            </p>
            <Link to="/blog">
              <Button variant="outline">Browse All Posts</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

export default SearchPage